import React, { useState, useEffect } from 'react';
import { useAuthHeader } from 'react-auth-kit';
import axios from 'axios';
import PropTypes from "prop-types";
import { Table, Badge, Button, Modal, Pagination, ButtonGroup, Form, Spinner, Accordion, Card, Row, Col, Alert } from 'react-bootstrap';
import * as Icon from 'react-feather';
import { mexicoCityNowDateTimeLocal, mexicoCityToday, parseDateOnlyAsMexicoCityDate } from '../../utils/dateTime';

function ClientPayments(props)  {
    const authHeader = useAuthHeader();

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalIva, setTotalIva] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [dateFilter, setDateFilter] = useState('max');
    const [sendingReminders, setSendingReminders] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [reminderResult, setReminderResult] = useState(null);
    const [expandedTrip, setExpandedTrip] = useState(null);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [loadingSchedule, setLoadingSchedule] = useState(false);
    const [savingSchedule, setSavingSchedule] = useState(false);
    const [scheduleError, setScheduleError] = useState('');
    const [scheduleSuccess, setScheduleSuccess] = useState('');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPaymentTrip, setSelectedPaymentTrip] = useState(null);
    const [paymentActionLoading, setPaymentActionLoading] = useState(false);
    const [paymentActionError, setPaymentActionError] = useState('');
    const [paymentComplementForm, setPaymentComplementForm] = useState({
        payment_date: mexicoCityNowDateTimeLocal(),
        payment_form: '03',
        currency: 'MXN',
        amount: '',
        send_email: false,
        email_recipients: '',
    });
    const [reminderSchedule, setReminderSchedule] = useState({
        enabled: true,
        recipients: [],
        days_of_week: ['fri'],
        hour: 12,
        minute: 0,
        timezone: 'America/Mexico_City',
        last_sent_date: null,
    });
    const apiUrl = process.env.REACT_APP_URL;
    const dayOptions = [
        { value: 'mon', label: 'Lun' },
        { value: 'tue', label: 'Mar' },
        { value: 'wed', label: 'Mie' },
        { value: 'thu', label: 'Jue' },
        { value: 'fri', label: 'Vie' },
        { value: 'sat', label: 'Sab' },
        { value: 'sun', label: 'Dom' },
    ];

    const get_total = (payments) => {
        let total = 0.0;
        let totalIva = 0.0;
        for (let i = 0; i < payments.length; i++) {
            total+= parseFloat(payments[i].price);
            totalIva+= parseFloat(payments[i].price) + (parseFloat(payments[i].price)) * .16 - (parseFloat(payments[i].price) * .04)
        }
        setTotal(total);
        setTotalIva(totalIva);

    }

    const get_all_trips = () => {
        axios({
            method:'GET',
            url:apiUrl+'/finance/get_client_payments',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setData(response.data.msg.Payments);
            get_total(response.data.msg.Payments);
        })
        .catch(error => {
            alert(error);
            console.log(error);
        });
    }

    const handlePaymentClick = (id, paid) => {
        setPaymentActionLoading(true);
        setPaymentActionError('');
        axios({
            method:'PUT',
            url:apiUrl+'/trips/update_paid',
            headers: {
                Authorization: authHeader()
            },
            data: {
                id: id,
                paid: paid,
            }
        }).then(response => {
            setShowPaymentModal(false);
            setSelectedPaymentTrip(null);
            get_all_trips();
        })
        .catch(error => {
            setPaymentActionError(error.response?.data?.error || 'No se pudo marcar el viaje como pagado.');
            console.log(error);
        })
        .finally(() => {
            setPaymentActionLoading(false);
        });
    }

    const getTripPaymentTotal = (trip) => {
        const subtotal = parseFloat(trip?.price || 0);
        return subtotal + (subtotal * 0.16) - (subtotal * 0.04);
    };

    const formatCurrency = (value) => {
        return `$${Number(value || 0).toLocaleString('es-MX', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const openPaymentModal = (trip) => {
        setSelectedPaymentTrip(trip);
        setPaymentActionError('');
        setPaymentComplementForm({
            payment_date: mexicoCityNowDateTimeLocal(),
            payment_form: '03',
            currency: 'MXN',
            amount: getTripPaymentTotal(trip).toFixed(2),
            send_email: false,
            email_recipients: trip.client_email || '',
        });
        setShowPaymentModal(true);
    };

    const updatePaymentComplementField = (field, value) => {
        setPaymentComplementForm(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const generatePaymentComplement = () => {
        if (!selectedPaymentTrip) {
            return;
        }
        setPaymentActionLoading(true);
        setPaymentActionError('');
        axios({
            method: 'POST',
            url: apiUrl + `/cartaporte/payment-complement/generate/${selectedPaymentTrip.id}`,
            headers: {
                Authorization: authHeader()
            },
            data: {
                payment_date: paymentComplementForm.payment_date,
                payment_form: paymentComplementForm.payment_form,
                currency: paymentComplementForm.currency,
                amount: paymentComplementForm.amount,
                send_email: paymentComplementForm.send_email,
                email_recipients: paymentComplementForm.email_recipients,
            }
        }).then(response => {
            setShowPaymentModal(false);
            setSelectedPaymentTrip(null);
            get_all_trips();
        }).catch(error => {
            const errData = error.response?.data;
            const detailErrors = errData?.details?.errors;
            setPaymentActionError(
                Array.isArray(detailErrors)
                    ? detailErrors.join(' ')
                    : (errData?.details || errData?.error || 'No se pudo generar el complemento de pago.')
            );
            console.log(error);
        }).finally(() => {
            setPaymentActionLoading(false);
        });
    };

    function addDaysToDate(dateString, daysToAdd) {
        const date = parseDateOnlyAsMexicoCityDate(dateString);
        if (!date) {
            return '';
        }
        date.setDate(date.getDate() + daysToAdd);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
        }

    const getOverdueCount = () => {
        const today = mexicoCityToday();
        return data.filter(trip => {
            const paymentDueDate = addDaysToDate(trip.formatted_date, trip.days_to_pay);
            if (!paymentDueDate) {
                return false;
            }
            return today > paymentDueDate;
        }).length;
    };

    const sendOverdueReminders = () => {
        setSendingReminders(true);
        setReminderResult(null);
        axios({
            method: 'GET',
            url: apiUrl + '/finance/email_client_unpaid_trips',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            const result = response.data;
            setReminderResult({
                success: true,
                message: result.msg,
                emailsSent: result.emails_sent,
            });
        })
        .catch(error => {
            const errData = error.response?.data;
            setReminderResult({
                success: false,
                message: errData?.error || errData?.msg || 'Error al enviar los recordatorios. Intente de nuevo.',
            });
            console.log(error);
        })
        .finally(() => {
            setSendingReminders(false);
        });
    };

    const getReminderSchedule = () => {
        setLoadingSchedule(true);
        setScheduleError('');
        axios({
            method: 'GET',
            url: apiUrl + '/finance/payment_reminder_settings',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setReminderSchedule(response.data.msg.payment_reminder_schedule);
        }).catch(error => {
            setScheduleError(error.response?.data?.error || 'No se pudo cargar la configuración de recordatorios.');
            console.log(error);
        }).finally(() => {
            setLoadingSchedule(false);
        });
    };

    const openScheduleModal = () => {
        setShowScheduleModal(true);
        setScheduleSuccess('');
        getReminderSchedule();
    };

    const updateScheduleField = (field, value) => {
        setReminderSchedule(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const toggleScheduleDay = (day) => {
        setReminderSchedule(prev => {
            const days = prev.days_of_week || [];
            const nextDays = days.includes(day)
                ? days.filter(item => item !== day)
                : [...days, day];
            return {
                ...prev,
                days_of_week: nextDays,
            };
        });
    };

    const getScheduleTimeValue = () => {
        const hour = String(reminderSchedule.hour ?? 12).padStart(2, '0');
        const minute = String(reminderSchedule.minute ?? 0).padStart(2, '0');
        return `${hour}:${minute}`;
    };

    const updateScheduleTime = (value) => {
        const [hour = '12', minute = '00'] = value.split(':');
        setReminderSchedule(prev => ({
            ...prev,
            hour: Number(hour),
            minute: Number(minute),
        }));
    };

    const saveReminderSchedule = () => {
        setSavingSchedule(true);
        setScheduleError('');
        setScheduleSuccess('');

        const recipients = (reminderSchedule.recipients || [])
            .join(',')
            .split(',')
            .map(email => email.trim())
            .filter(Boolean);

        if (recipients.length === 0) {
            setSavingSchedule(false);
            setScheduleError('Agrega al menos un correo destinatario.');
            return;
        }

        if (!reminderSchedule.days_of_week || reminderSchedule.days_of_week.length === 0) {
            setSavingSchedule(false);
            setScheduleError('Selecciona al menos un dia de envío.');
            return;
        }

        const payload = {
            payment_reminder_schedule: {
                ...reminderSchedule,
                recipients: recipients,
                hour: Number(reminderSchedule.hour),
                minute: Number(reminderSchedule.minute),
            }
        };

        axios({
            method: 'PUT',
            url: apiUrl + '/finance/payment_reminder_settings',
            headers: {
                Authorization: authHeader()
            },
            data: payload,
        }).then(response => {
            const result = response.data.msg;
            setReminderSchedule(result.payment_reminder_schedule);
            setScheduleSuccess(result.triggered ? 'Configuración guardada y recordatorio ejecutado.' : 'Configuración guardada.');
            if (result.triggered) {
                setReminderResult({
                    success: true,
                    message: result.trigger_response,
                    emailsSent: null,
                });
                setShowReminderModal(true);
            }
        }).catch(error => {
            setScheduleError(error.response?.data?.error || 'No se pudo guardar la configuración.');
            console.log(error);
        }).finally(() => {
            setSavingSchedule(false);
        });
    };

    useEffect(() => {
        get_all_trips();
        getReminderSchedule();
    }, []);

    // Filter data based on date range
    useEffect(() => {
        if (data.length === 0) {
            setFilteredData([]);
            return;
        }

        const filterByDateRange = () => {
            const today = parseDateOnlyAsMexicoCityDate(mexicoCityToday());
            today.setHours(0, 0, 0, 0);

            let startDate;

            switch(dateFilter) {
                case '1week':
                    startDate = new Date(today);
                    startDate.setDate(today.getDate() - 7);
                    break;
                case '1month':
                    startDate = new Date(today);
                    startDate.setMonth(today.getMonth() - 1);
                    break;
                case '6months':
                    startDate = new Date(today);
                    startDate.setMonth(today.getMonth() - 6);
                    break;
                case 'ytd':
                    startDate = new Date(today.getFullYear(), 0, 1);
                    break;
                case '1year':
                    startDate = new Date(today);
                    startDate.setFullYear(today.getFullYear() - 1);
                    break;
                case '5years':
                    startDate = new Date(today);
                    startDate.setFullYear(today.getFullYear() - 5);
                    break;
                case 'max':
                default:
                    setFilteredData(data);
                    get_total(data);
                    return;
            }

            const filtered = data.filter(trip => {
                const tripDate = parseDateOnlyAsMexicoCityDate(trip.formatted_date);
                return tripDate >= startDate && tripDate <= today;
            });

            setFilteredData(filtered);
            get_total(filtered);
            setCurrentPage(1);
        };

        filterByDateRange();
    }, [data, dateFilter]);

    const handleDateFilterChange = (filter) => {
        setDateFilter(filter);
    };

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Generate pagination items
    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        // Always show first page
        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={1}
                    active={1 === currentPage}
                    onClick={() => handlePageChange(1)}
                >
                    1
                </Pagination.Item>
            );
        }

        // Show ellipsis if needed before current range
        if (currentPage > 3) {
            items.push(<Pagination.Ellipsis key="start-ellipsis" />);
        }

        // Show pages around current page
        const startPage = Math.max(2, currentPage - 1);
        const endPage = Math.min(totalPages - 1, currentPage + 1);

        for (let page = startPage; page <= endPage; page++) {
            if (page !== 1 && page !== totalPages) {
                items.push(
                    <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                    >
                        {page}
                    </Pagination.Item>
                );
            }
        }

        // Show ellipsis if needed after current range
        if (currentPage < totalPages - 2) {
            items.push(<Pagination.Ellipsis key="end-ellipsis" />);
        }

        // Always show last page if more than one page
        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage}
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

        return (
            <>
            <div className="card mb-4">
                <div className="card-header bg-light">
                    <div className="row">
                        <div className="col-md-6">
                            <h5 className="mb-0">Resumen de Cobros</h5>
                        </div>
                        <div className="col-md-6 text-end">
                            <div className="d-flex justify-content-end align-items-center">
                                <div className="me-4">
                                    <span className="text-muted">Subtotal: </span>
                                    <strong className="text-dark">${total.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                </div>
                                <div>
                                    <span className="text-muted">Total: </span>
                                    <strong className="text-success fs-5">${totalIva.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {/* Date Range Filter */}
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-2 align-items-center">
                            <Button
                                size="sm"
                                variant="outline-danger"
                                disabled={sendingReminders || getOverdueCount() === 0}
                                onClick={() => setShowReminderModal(true)}
                            >
                                {sendingReminders ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" className="me-1" />
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        <Icon.Mail size={14} className="me-1" />
                                        Enviar Recordatorios ({getOverdueCount()})
                                    </>
                                )}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={openScheduleModal}
                            >
                                <Icon.Settings size={14} className="me-1" />
                                Programación
                            </Button>
                            <Badge bg={reminderSchedule.enabled ? 'success' : 'secondary'}>
                                {reminderSchedule.enabled ? 'Auto activo' : 'Auto apagado'}
                            </Badge>
                        </div>
                        <div className="d-flex align-items-center">
                        <Form.Label className="me-2 mb-0" style={{whiteSpace: 'nowrap'}}>Filtrar por Fecha:</Form.Label>
                        <ButtonGroup>
                            <Button
                                size="sm"
                                variant={dateFilter === '1week' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('1week')}
                            >
                                1 Semana
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === '1month' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('1month')}
                            >
                                1 Mes
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === '6months' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('6months')}
                            >
                                6 Meses
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === 'ytd' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('ytd')}
                            >
                                YTD
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === '1year' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('1year')}
                            >
                                1 Año
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === '5years' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('5years')}
                            >
                                5 Años
                            </Button>
                            <Button
                                size="sm"
                                variant={dateFilter === 'max' ? 'primary' : 'outline-primary'}
                                onClick={() => handleDateFilterChange('max')}
                            >
                                Todo
                            </Button>
                        </ButtonGroup>
                        </div>
                    </div>

                    {/* <div className="card-header d-flex justify-content-between">
                        <h5 className="card-title">Registros</h5>
                        <AddTrips setData={get_all_trips}/>
                    </div> */}

                    {/* <div className="height-310"> */}
                    <div>
                        <Table className="m-0" responsive>
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th>Factura</th>
                                    <th>CFDITRASLADO</th>
                                    <th>Días CXP</th>
                                    <th>Subtotal</th>
                                    <th>IVA Trasladado</th>
                                    <th>IVA Retenido</th>
                                    <th>Total</th>
                                    <th>Carta Porte</th>
                                    <th>Fecha entrega de evidencias</th>
                                    <th>Fecha a pagar</th>
                                    <th>Estado</th>
                                    <th>Pagado</th>
                                    {/* <th>Fecha Pagado</th> */}
                                </tr>
                            </thead>
                            <tbody>
        {currentItems.map(trip => {
          const paymentDueDate = addDaysToDate(trip.formatted_date, trip.days_to_pay);

          // Get today's date in YYYY-MM-DD format
          const today = mexicoCityToday();

          // Determine badge variant based on payment due date comparison
          let badgeVariant = paymentDueDate ? 'primary' : 'secondary'; // Default for dates in the future
          if (paymentDueDate && today === paymentDueDate) {
            badgeVariant = 'warning'; // Due today
          } else if (paymentDueDate && today > paymentDueDate) {
            badgeVariant = 'danger'; // Past due
          }

          return (
            <React.Fragment key={trip.id}>
            <tr>
              <td>{trip.company_name}</td>
              <td>{trip.invoice_id}</td>
              <td>{trip.cfditraslado}</td>
              <td>{trip.days_to_pay}</td>
              <td>${trip.price}</td>
              <td>${(parseFloat(trip.price) * .16)}</td>
              <td>${(parseFloat(trip.price) * .04)}</td>
              <td>${(parseFloat(trip.price) + (parseFloat(trip.price)) * .16 - (parseFloat(trip.price) * .04))}</td>
              <td>Si</td>
              <td>{trip.formatted_date}</td>
              <td>{paymentDueDate}
              </td>
              <td>
                <Badge bg={badgeVariant}>Pendiente</Badge>
              </td>
              <td>
                <div className="d-flex gap-1">
                    {((trip.maneuver_records && trip.maneuver_records.length > 0) || (trip.stay_records && trip.stay_records.length > 0)) && (
                        <Button size="sm" variant="outline-info" onClick={() => setExpandedTrip(expandedTrip === trip.id ? null : trip.id)}>
                            <Icon.ChevronDown size={14} style={{ transform: expandedTrip === trip.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </Button>
                    )}
                    <Button size="sm" variant="outline-success" onClick={() => {
                            openPaymentModal(trip);
                        }}>
                            Marcar Pagado
                        </Button>
                </div>
              </td>

            </tr>
            {expandedTrip === trip.id && (
                <tr>
                    <td colSpan="13" className="p-0">
                        <div className="p-3 bg-light">
                            {trip.maneuver_records && trip.maneuver_records.length > 0 && (
                                <div className="mb-3">
                                    <h6 className="mb-2"><Icon.Tool size={16} className="me-1" /> Maniobras ({trip.maneuver_records.length})</h6>
                                    <Table size="sm" bordered className="bg-white mb-0">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Factura</th>
                                                <th>Descripcion</th>
                                                <th>Subtotal</th>
                                                <th>IVA (16%)</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trip.maneuver_records.map((m, i) => (
                                                <tr key={`m-${m.id}`}>
                                                    <td>{i + 1}</td>
                                                    <td>{m.invoice_id || '-'}</td>
                                                    <td>{m.description || '-'}</td>
                                                    <td>${parseFloat(m.subtotal || 0).toFixed(2)}</td>
                                                    <td>${parseFloat(m.iva || 0).toFixed(2)}</td>
                                                    <td><strong>${parseFloat(m.total || 0).toFixed(2)}</strong></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                            {trip.stay_records && trip.stay_records.length > 0 && (
                                <div>
                                    <h6 className="mb-2"><Icon.Clock size={16} className="me-1" /> Estadias ({trip.stay_records.length})</h6>
                                    <Table size="sm" bordered className="bg-white mb-0">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Factura</th>
                                                <th>Descripcion</th>
                                                <th>Subtotal</th>
                                                <th>IVA (16%)</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trip.stay_records.map((s, i) => (
                                                <tr key={`s-${s.id}`}>
                                                    <td>{i + 1}</td>
                                                    <td>{s.invoice_id || '-'}</td>
                                                    <td>{s.description || '-'}</td>
                                                    <td>${parseFloat(s.subtotal || 0).toFixed(2)}</td>
                                                    <td>${parseFloat(s.iva || 0).toFixed(2)}</td>
                                                    <td><strong>${parseFloat(s.total || 0).toFixed(2)}</strong></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    </td>
                </tr>
            )}
            </React.Fragment>
          );
        })}
    </tbody>
                        </Table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="text-muted small">
                                Mostrando {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredData.length)} de {filteredData.length} entradas
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.Prev
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                />
                                {getPaginationItems()}
                                <Pagination.Next
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                />
                            </Pagination>
                        </div>
                    )}
                </div>
            </div>

            {/* Reminder Schedule Modal */}
            <Modal show={showScheduleModal} onHide={() => setShowScheduleModal(false)} centered size="lg">
                <Modal.Header closeButton={!savingSchedule}>
                    <Modal.Title>Programación de Recordatorios</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingSchedule ? (
                        <div className="py-4 text-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Cargando configuración...
                        </div>
                    ) : (
                        <>
                            {scheduleError && <Alert variant="danger">{scheduleError}</Alert>}
                            {scheduleSuccess && <Alert variant="success">{scheduleSuccess}</Alert>}
                            <Form>
                                <Form.Check
                                    type="switch"
                                    id="payment-reminder-enabled"
                                    label="Enviar recordatorios automáticos"
                                    checked={Boolean(reminderSchedule.enabled)}
                                    disabled={savingSchedule}
                                    onChange={(event) => updateScheduleField('enabled', event.target.checked)}
                                    className="mb-3"
                                />
                                <Form.Group className="mb-3" controlId="payment-reminder-recipients">
                                    <Form.Label>Destinatarios</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={(reminderSchedule.recipients || []).join(', ')}
                                        disabled={savingSchedule}
                                        placeholder="correo@empresa.com, otro@empresa.com"
                                        onChange={(event) => updateScheduleField(
                                            'recipients',
                                            event.target.value.split(',').map(email => email.trim()).filter(Boolean)
                                        )}
                                    />
                                    <Form.Text className="text-muted">
                                        Se enviará un correo por cliente con pagos vencidos a cada destinatario configurado.
                                    </Form.Text>
                                </Form.Group>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="payment-reminder-timezone">
                                            <Form.Label>Zona horaria</Form.Label>
                                            <Form.Select
                                                value={reminderSchedule.timezone || 'America/Mexico_City'}
                                                disabled={savingSchedule}
                                                onChange={(event) => updateScheduleField('timezone', event.target.value)}
                                            >
                                                <option value="America/Mexico_City">Ciudad de México</option>
                                                <option value="UTC">UTC</option>
                                                <option value="America/Chicago">Central US</option>
                                                <option value="America/New_York">Eastern US</option>
                                                <option value="America/Los_Angeles">Pacific US</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="payment-reminder-time">
                                            <Form.Label>Horario</Form.Label>
                                            <Form.Control
                                                type="time"
                                                value={getScheduleTimeValue()}
                                                disabled={savingSchedule}
                                                onChange={(event) => updateScheduleTime(event.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label>Días de envío</Form.Label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {dayOptions.map(day => (
                                            <Button
                                                key={day.value}
                                                size="sm"
                                                variant={(reminderSchedule.days_of_week || []).includes(day.value) ? 'primary' : 'outline-primary'}
                                                disabled={savingSchedule}
                                                onClick={() => toggleScheduleDay(day.value)}
                                            >
                                                {day.label}
                                            </Button>
                                        ))}
                                    </div>
                                </Form.Group>
                                {reminderSchedule.last_sent_date && (
                                    <div className="text-muted small">
                                        Último envío automático: {reminderSchedule.last_sent_date}
                                    </div>
                                )}
                            </Form>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowScheduleModal(false)} disabled={savingSchedule}>
                        Cerrar
                    </Button>
                    <Button variant="primary" onClick={saveReminderSchedule} disabled={savingSchedule || loadingSchedule}>
                        {savingSchedule ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" className="me-1" />
                                Guardando...
                            </>
                        ) : 'Guardar'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal */}
            <Modal show={showReminderModal && !reminderResult} onHide={() => setShowReminderModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Envío de Recordatorios</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Se enviará un correo electrónico de recordatorio de pago vencido a los destinatarios configurados.</p>
                    <p className="mb-2"><strong>Destinatarios:</strong> {(reminderSchedule.recipients || []).join(', ') || 'Sin destinatarios configurados'}</p>
                    <p className="mb-0"><strong className="text-danger">{getOverdueCount()} pago(s) vencido(s)</strong> encontrados.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowReminderModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="danger"
                        disabled={sendingReminders}
                        onClick={() => sendOverdueReminders()}
                    >
                        {sendingReminders ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" className="me-1" />
                                Enviando...
                            </>
                        ) : (
                            <>
                                <Icon.Send size={14} className="me-1" />
                                Enviar Recordatorios
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Result Modal */}
            <Modal show={showReminderModal && reminderResult !== null} onHide={() => { setShowReminderModal(false); setReminderResult(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {reminderResult?.success ? '✅ Recordatorios Enviados' : '❌ Error'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{reminderResult?.message}</p>
                    {reminderResult?.success && reminderResult?.emailsSent > 0 && (
                        <Badge bg="success" className="fs-6">
                            {reminderResult.emailsSent} correo(s) enviado(s)
                        </Badge>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => { setShowReminderModal(false); setReminderResult(null); }}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Mark Paid / Payment Complement Modal */}
            <Modal show={showPaymentModal} onHide={() => !paymentActionLoading && setShowPaymentModal(false)} size="xl">
                <Modal.Header closeButton={!paymentActionLoading}>
                    <Modal.Title>Registrar pago</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {paymentActionError && <Alert variant="danger">{paymentActionError}</Alert>}
                    {selectedPaymentTrip && (
                        <div>
                            <Card className="mb-3">
                                <Card.Header><h6 className="mb-0">Resumen</h6></Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={3}><strong>Cliente:</strong> {selectedPaymentTrip.company_name}</Col>
                                        <Col md={3}><strong>Factura:</strong> {selectedPaymentTrip.invoice_id || 'Sin factura'}</Col>
                                        <Col md={3}><strong>CFDI Traslado:</strong> {selectedPaymentTrip.cfditraslado || 'N/A'}</Col>
                                        <Col md={3}><strong>Total:</strong> <span className="text-success fw-bold">{formatCurrency(getTripPaymentTotal(selectedPaymentTrip))}</span></Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            <Card className="mb-3">
                                <Card.Header><h6 className="mb-0">Complemento de Pago</h6></Card.Header>
                                <Card.Body>
                                    <Table size="sm" bordered>
                                        <thead>
                                            <tr>
                                                <th>Fecha de Pago</th>
                                                <th>Forma de Pago SAT</th>
                                                <th>Moneda</th>
                                                <th>Monto Pagado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    <Form.Control
                                                        type="datetime-local"
                                                        value={paymentComplementForm.payment_date}
                                                        disabled={paymentActionLoading}
                                                        onChange={(event) => updatePaymentComplementField('payment_date', event.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Select
                                                        value={paymentComplementForm.payment_form}
                                                        disabled={paymentActionLoading}
                                                        onChange={(event) => updatePaymentComplementField('payment_form', event.target.value)}
                                                    >
                                                        <option value="03">03 - Transferencia electrónica</option>
                                                        <option value="01">01 - Efectivo</option>
                                                        <option value="02">02 - Cheque nominativo</option>
                                                        <option value="04">04 - Tarjeta de crédito</option>
                                                        <option value="28">28 - Tarjeta de débito</option>
                                                    </Form.Select>
                                                </td>
                                                <td>
                                                    <Form.Select
                                                        value={paymentComplementForm.currency}
                                                        disabled={paymentActionLoading}
                                                        onChange={(event) => updatePaymentComplementField('currency', event.target.value)}
                                                    >
                                                        <option value="MXN">MXN</option>
                                                        <option value="USD">USD</option>
                                                    </Form.Select>
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={paymentComplementForm.amount}
                                                        disabled={paymentActionLoading}
                                                        onChange={(event) => updatePaymentComplementField('amount', event.target.value)}
                                                    />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>

                            <Card className="mb-3">
                                <Card.Header><h6 className="mb-0">Enviar por Correo</h6></Card.Header>
                                <Card.Body>
                                    <Form.Check
                                        type="switch"
                                        id="payment-complement-send-email"
                                        label="Enviar complemento por correo"
                                        checked={Boolean(paymentComplementForm.send_email)}
                                        disabled={paymentActionLoading}
                                        onChange={(event) => updatePaymentComplementField('send_email', event.target.checked)}
                                        className="mb-3"
                                    />
                                    {paymentComplementForm.send_email && (
                                        <Form.Group controlId="payment-complement-email-recipients">
                                            <Form.Label>Destinatario(s)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={paymentComplementForm.email_recipients}
                                                disabled={paymentActionLoading}
                                                placeholder="cliente@empresa.com, otro@empresa.com"
                                                onChange={(event) => updatePaymentComplementField('email_recipients', event.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                Se usará el correo del cliente por defecto; puedes editarlo antes de generar.
                                            </Form.Text>
                                        </Form.Group>
                                    )}
                                </Card.Body>
                            </Card>

                            <div className="d-flex justify-content-between align-items-center">
                                <Button
                                    variant="outline-success"
                                    disabled={paymentActionLoading}
                                    onClick={() => handlePaymentClick(selectedPaymentTrip.id, true)}
                                >
                                    {paymentActionLoading ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" role="status" className="me-1" />
                                            Guardando...
                                        </>
                                    ) : 'Solo marcar como pagado'}
                                </Button>
                                <Button
                                    variant="primary"
                                    disabled={paymentActionLoading}
                                    onClick={generatePaymentComplement}
                                >
                                    {paymentActionLoading ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" role="status" className="me-1" />
                                            Generando...
                                        </>
                                    ) : 'Generar complemento de pago'}
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
            </>
        );

}

//Status: Entregado, Pendiente, Cancelado, En camino
// Success, Warning, Danger, Info
//https://react-bootstrap.netlify.app/docs/components/badge/
export default ClientPayments;
